import { useState, useEffect, useCallback } from 'react';
import { useMatch, useSearchParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { FallbackProps } from 'react-error-boundary';
import { Skeleton } from '@antimatterhq/ui/components/skeleton';
import Spacer from '@antimatterhq/ui/components/spacer';
import { GlobalError } from '@antimatterhq/ui/components/globalError';
import { DatasourceUpgradeSidebar } from './_partials/DatasourceUpgradeSidebar';
import { DatasourceUpgradeReview } from './_partials/DatasourceUpgradeReview';
import { DatasourceUpgradeProvider } from '@/contexts/DatasourceUpgradeContext';
import { SuspenseErrorBoundary } from '@/components/errorBoundary/SuspenseErrorBoundary';
import { TwoColumnLayout } from '@/components/container';
import { usePresetUpgrade } from '@/hooks/components/usePresetUpgrade';

function DatasourceUpgradeSuspense() {
  const match = useMatch('/configure/data-sources/upgrade/:name');
  const { name = '' } = match?.params || {};
  const [searchParams] = useSearchParams();
  const upgradePresetVersion = searchParams.get('upgradePresetVersion');
  const [drawerHeight, setDrawerHeight] = useState(399);
  const [contentHeight, setContentHeight] = useState(0);

  // Calculate dynamic height based on window height - drawer height - 86
  const calculateHeight = useCallback(() => {
    const height = window.innerHeight - drawerHeight - 86 - 16;
    setContentHeight(Math.max(height, 300)); // Minimum height of 300px
  }, [drawerHeight]);

  // Set up window resize listener and calculate initial height
  useEffect(() => {
    calculateHeight();

    const handleResize = () => {
      calculateHeight();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [drawerHeight, calculateHeight]);

  const {
    preset,
    upgradePreset,
    isPresetPending,
    isUpgradePresetPending,
    datasource,
  } = usePresetUpgrade({
    name,
    upgradePresetVersion,
  });

  if (
    (isPresetPending && datasource.spec.usePreset) ||
    (isUpgradePresetPending && upgradePresetVersion)
  ) {
    return <DatasourceUpgradeSuspenseFallback />;
  }

  if (!preset || !upgradePreset) {
    throw new Error('Error loading upgrade preset');
  }

  return (
    <DatasourceUpgradeProvider
      currentPreset={preset}
      upgradePreset={upgradePreset}
      datasource={datasource}
      data-testid={`upgrade-datasource-provider-${name}`}
    >
      <div
        data-testid={`upgrade-datasource-page-${name}`}
        className="overflow-y-auto custom-scrollbar"
        style={{ height: `${contentHeight}px` }}
      >
        <TwoColumnLayout.Root>
          <TwoColumnLayout.LeftColumn>
            <DatasourceUpgradeReview setDrawerHeight={setDrawerHeight} />
          </TwoColumnLayout.LeftColumn>
          <TwoColumnLayout.RightColumn>
            <DatasourceUpgradeSidebar drawerHeight={drawerHeight} />
          </TwoColumnLayout.RightColumn>
        </TwoColumnLayout.Root>
      </div>
    </DatasourceUpgradeProvider>
  );
}

const DataSourceUpgradeErrorBoundaryFallback = ({
  error,
  resetErrorBoundary,
}: FallbackProps) => {
  const match = useMatch('/configure/data-sources/upgrade/:name');
  const { name = '' } = match?.params || {};

  const isNotFoundError = isAxiosError(error) && error.response?.status === 404;

  if (isNotFoundError)
    return (
      <GlobalError
        title="Datasource not found"
        description={`Datasource "${name}" could not be found`}
        learnMoreLink="/configure/data-sources"
        learnMoreText="Go to datasources"
        onRetry={async () => resetErrorBoundary()}
        retryText="Retry"
        className="h-[75vh]"
        data-testid={`datasource-not-found-error-${name}`}
      />
    );

  const errorMessage = isAxiosError(error)
    ? (error.response?.statusText ?? error.message)
    : error.message;

  return (
    <GlobalError
      title="Error upgrading datasource"
      description={errorMessage}
      onRetry={async () => resetErrorBoundary()}
      retryText="Retry"
      className="h-[75vh]"
      data-testid={`datasource-loading-error-${name}`}
    />
  );
};

function DatasourceUpgradeSuspenseFallback() {
  return (
    <div
      className="flex h-full w-full flex-col p-4"
      data-testid="upgrade-datasource-loading"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <Skeleton
            className="h-14 w-56"
            data-testid="upgrade-datasource-title-skeleton"
          />
          <Skeleton
            className="h-10 w-full max-w-[850px]"
            data-testid="upgrade-datasource-steps-skeleton"
          />
        </div>
        <Spacer size="sm" />
        <div
          className="flex max-w-[600px] flex-col gap-2"
          data-testid="upgrade-datasource-form-skeleton"
        >
          <Skeleton
            className="h-6 w-20"
            data-testid="upgrade-datasource-form-field-skeleton-1"
          />
          <Skeleton className="h-10 w-full" />

          <Skeleton
            className="h-6 w-20"
            data-testid="upgrade-datasource-form-field-skeleton-2"
          />
          <Skeleton className="h-28 w-full" />

          <Skeleton
            className="h-6 w-20"
            data-testid="upgrade-datasource-form-field-skeleton-3"
          />
          <Skeleton className="h-10 w-full" />

          <Skeleton
            className="h-6 w-20"
            data-testid="upgrade-datasource-form-field-skeleton-4"
          />
          <Skeleton className="h-10 w-20" />

          <Skeleton
            className="h-6 w-20"
            data-testid="upgrade-datasource-form-field-skeleton-5"
          />
          <Skeleton className="h-10 w-full" />

          <Skeleton
            className="h-6 w-20"
            data-testid="upgrade-datasource-form-field-skeleton-6"
          />
          <div
            className="flex gap-4"
            data-testid="upgrade-datasource-buttons-skeleton"
          >
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DatasourceUpgrade() {
  return (
    <SuspenseErrorBoundary
      suspenseFallback={<DatasourceUpgradeSuspenseFallback />}
      errorBoundaryFallback={DataSourceUpgradeErrorBoundaryFallback}
      data-testid="upgrade-datasource-error-boundary"
    >
      <DatasourceUpgradeSuspense />
    </SuspenseErrorBoundary>
  );
}
